import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

export type PaginatedListLoadingProps = {
  className?: string;
  /** Minimum height for the loading panel (ignored when `tableColSpan` is set). */
  minHeightClass?: string;
  /** Render as a single table row spanning this many columns. */
  tableColSpan?: number;
  /** Override headline (defaults to `common.list_loading_title`). */
  title?: string;
  /** Override subline; pass empty string to hide (defaults to `common.list_loading_hint`). */
  hint?: string;
  /** Shimmer placeholder rows below the message (panel mode only). */
  skeletonRows?: number;
  /** Less padding — modals, side panels, dense layouts. */
  compact?: boolean;
};

/**
 * Shared loading UI for paginated lists and slow network: spinner, copy, optional skeleton stripes.
 */
export function PaginatedListLoading({
  className = '',
  minHeightClass = 'min-h-[min(320px,52vh)]',
  tableColSpan,
  title,
  hint,
  skeletonRows = 0,
  compact = false,
}: PaginatedListLoadingProps) {
  const { t } = useTranslation();
  const titleText = title ?? t('common.list_loading_title');
  const hintText = hint === '' ? null : (hint ?? t('common.list_loading_hint'));

  const inner = (
    <div
      className={[
        'flex flex-col items-center justify-center text-center',
        compact ? 'gap-3 px-3 py-6' : 'gap-4 px-4 py-10',
        tableColSpan ? 'py-8' : '',
      ].join(' ')}
    >
      <motion.div
        className="rounded-full bg-emerald-50/90 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] ring-1 ring-emerald-100/80"
        animate={{ scale: [1, 1.04, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Loader2
          className="h-9 w-9 animate-spin text-[#00b853] motion-reduce:animate-none motion-reduce:opacity-80 sm:h-10 sm:w-10"
          strokeWidth={2}
          aria-hidden
        />
        <span className="sr-only">{titleText}</span>
      </motion.div>

      <div className="max-w-md space-y-1.5">
        <p className="text-sm font-semibold text-slate-800 sm:text-base">{titleText}</p>
        {hintText ? (
          <p className="text-xs leading-relaxed text-slate-500 sm:text-sm">{hintText}</p>
        ) : null}
      </div>

      {skeletonRows > 0 && !tableColSpan ? (
        <div className="mt-2 w-full max-w-lg space-y-2.5">
          {Array.from({ length: skeletonRows }).map((_, i) => (
            <div
              key={`sk-${i}`}
              className="h-11 w-full animate-pulse rounded-xl bg-gradient-to-r from-slate-100 via-slate-200/70 to-slate-100"
              style={{ animationDelay: `${i * 90}ms` }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );

  const panel = (
    <div
      className={[
        'overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/90 shadow-sm',
        tableColSpan ? '' : minHeightClass,
        className,
      ].join(' ')}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {inner}
    </div>
  );

  if (tableColSpan != null) {
    return (
      <tr>
        <td colSpan={tableColSpan} className="p-0">
          {panel}
        </td>
      </tr>
    );
  }

  return panel;
}

/** Apply `className="relative"` on the parent. Shows a soft overlay while refreshing with existing rows visible. */
export function ListLoadingRefreshOverlay({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div
      className={[
        'pointer-events-none absolute inset-0 z-10 flex items-start justify-center rounded-2xl bg-white/65 pt-6 backdrop-blur-[3px] sm:pt-10',
        className,
      ].join(' ')}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="pointer-events-auto flex items-center gap-2.5 rounded-full border border-slate-200/90 bg-white/95 px-4 py-2.5 shadow-lg shadow-slate-900/10">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#00b853]" strokeWidth={2.5} aria-hidden />
        <span className="text-sm font-medium text-slate-700">{t('common.list_loading_title')}</span>
      </div>
    </div>
  );
}
